import { Inject, Optional, Pipe, PipeTransform } from '@angular/core';
import { ConditionalFn, digger, DigOptions } from '@cbre/digger';
import {
  MIQQLParser,
  boolToString,
  formatDate,
  formatNumber,
  MiqMoment,
  EMPTY_STRING,
  DASH,
  traverseTree,
} from '@miq/utils';
import { Column, hasReadPath, InputType } from '@miq/catalog';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { DataObject } from '@shared/models/data-object.model';
import { getLovValueKey } from '@shared/utils/helpers/lov-datakey-factory';
import { simpleValueValidation } from '@shared/utils/helpers/records.helpers';
import { getGlobalDateDisplayFormat } from '@shared/utils/helpers/user-preferences';

const COMPANY_CONTACT_PATH = 'company_contact_role_addresses';
const RENT_ESCALATION_CUSTOM_PATH = 'rent_escalation_custom';

const FALLBACK_LOGGER: CbreLogger = {
  info: () => {},
  debug: () => {},
  warn: () => {},
  error: () => {},
};

const formatLoggerErrorMessage = (message: string, error?: unknown) =>
  error == null ? message : `${message}: ${String(error)}`;

let dataCellFormatterLogger: CbreLogger = FALLBACK_LOGGER;

const logDataCellFormatterError = (message: string, error?: unknown) =>
  dataCellFormatterLogger.error(formatLoggerErrorMessage(message, error));

const setDataCellFormatterLogger = (logger?: CbreLogger) => {
  dataCellFormatterLogger = logger ?? FALLBACK_LOGGER;
};

export const dateSanitizer = (value: string, toUtc?: boolean, extended?: boolean) => {
  const isValid = value != null && value !== '';
  const extendedFormat = extended ? 'h:mm:ss a' : '';
  let result = '';

  if (isValid) {
    const formattedDate = MiqMoment.formatFromInputFormat(
      value,
      MiqMoment.utcInputFormat,
      `MM/DD/YYYY ${extendedFormat}`,
      toUtc
    );
    const outputFormat = getGlobalDateDisplayFormat(undefined, extended);
    result = formatDate(formattedDate, outputFormat, 'en-US') as string;
  }
  return result;
};

const SANITIZER_MAP_BY_DATA_TYPE = {
  boolean: (value, opts?) => (opts.values ? value : boolToString(value)),
  integer: (value, opts?, defaultText?) =>
    formatNumber(
      value,
      'en-US',
      !!opts.options && !!opts.options.format ? opts.options.format : undefined,
      defaultText
    ),
  numeric: (value, opts?, defaultText?) =>
    formatNumber(
      value,
      'en-US',
      !!opts.options && !!opts.options.format ? opts.options.format : undefined,
      defaultText
    ),
};

const SANITIZER_MAP_BY_INPUT_TYPE = {
  Date: (value, opts?) => dateSanitizer(value, opts.dataType !== 'timestamp with time zone'),
};

const self = s => s;

export const sanitizeValue = (value, { dataType, inputType, values, options }: any, defaultText?) => {
  const sanitizer = SANITIZER_MAP_BY_INPUT_TYPE[inputType] || SANITIZER_MAP_BY_DATA_TYPE[dataType] || self;
  return sanitizer(value, { dataType, values, options }, defaultText);
};

type DCFColumn = Pick<
  Column,
  | 'header'
  | 'field'
  | 'path'
  | 'getReadPath'
  | 'condition'
  | 'values'
  | 'lookupValueKey'
  | 'dataType'
  | 'displayFn'
  | 'inputType'
  | 'options'
> & {
  rawData?: boolean;
  filterDuplicates?: boolean;
  replaceEmptyWith?: any;
  settings: any;
  mappingLOV: any;
  diggerOptions?: ConditionalFn | DigOptions;
};

/**
 * @description
 *
 * If provided, runs the display function, which shall return a value to be
 * displayed. Otherwise, it will return the result of the
 * [digger]{@link digger} helper.
 *
 * @param data any contains the data to be transformed.
 * @param field {string} used to extract data using digger.
 * @param rawData {boolean} determines whether just to extract data or to sanitize also.
 * @param condition {string} applies the condition while extracting the data
 * @param column {object} contains all other fields like inputType, dataType etc.
 */
@Pipe({
  name: 'dataCellFormatter',
  standalone: false,
})
export class DataCellFormatterPipe implements PipeTransform {
  constructor(@Optional() @Inject(CbreLoggerToken) logger?: CbreLogger) {
    if (logger != null) {
      setDataCellFormatterLogger(logger);
    }
  }
  /**
   * @param data object to get data from.
   * @param args special arguments.
   * @param _updated optional flag used to manually trigger the pipe.
   */
  transform(data: DataObject, args: Partial<DCFColumn> = {}, _updated?: boolean): any {
    return DataCellFormatterPipe.Transform(data, args);
  }

  public static Transform(data: Record<string, any>, args: Partial<DCFColumn>) {
    const { displayFn, settings = {}, mappingLOV } = args ?? {};
    try {
      if (displayFn && typeof displayFn === 'function') {
        let result = displayFn(data, settings, mappingLOV, args);
        if (result && typeof result === 'object' && result.value != null) {
          result = result.value;
        }
        return result;
      }
      return DataCellFormatterPipe.extractValue(data, args);
    } catch (e) {
      // this catch will keep ppt download working in case any field is error
      logDataCellFormatterError('DataCellFormatterPipe.Transform error', e);
      return args?.replaceEmptyWith ?? EMPTY_STRING;
    }
  }

  public static extractValue(
    data: any,
    column: Partial<DCFColumn>,
    lovValueFormatter?: (lovVal: DataObject) => string
  ): any {
    // return data as is if there is no column
    if (column == null) {
      return data;
    }

    const _valueWhenEmpty = column?.replaceEmptyWith ?? EMPTY_STRING;
    const { rawData = false, filterDuplicates = true } = column;
    let extractedData = extractData(data, column);

    if (extractedData == null) {
      return _valueWhenEmpty;
    }

    if (column.values?.length > 0) {
      const keyListAsArray = Array.isArray(extractedData) ? extractedData : [extractedData];
      const lovValues = getLOVValues(keyListAsArray, column, filterDuplicates, lovValueFormatter);
      if (lovValues.length > 0) {
        extractedData = lovValues;
      }
    }

    if (rawData) {
      return extractedData;
    }

    if (Array.isArray(extractedData)) {
      return formatExtractedDataWhenArray(extractedData, column, filterDuplicates, column?.replaceEmptyWith ?? DASH);
    }
    let options = column?.options ?? {};
    if (column['format'] && column['format'] != '') {
      options = { ...options, format: column['format'] };
    }
    return (
      sanitizeValue(extractedData, { ...column, options })
        .toString()
        .trim() || _valueWhenEmpty
    );
  }
}

const getLOVValues = (
  keyList: string[],
  column: Partial<DCFColumn>,
  filterDuplicates: boolean,
  lovValueFormatter: (params?: any) => any
) => {
  // Get the Lov key could be key or value depending on the Column
  const dataKey = getLovValueKey(column);
  // Get the list of the values from the LOV options
  return filterDuplicates
    ? getUniqueLOVValues(keyList, dataKey, column, lovValueFormatter)
    : getAllLovValues(keyList, dataKey, column, lovValueFormatter, column?.replaceEmptyWith ?? DASH);
};

const getUniqueLOVValues = (
  keyList: any[],
  dataKey: string,
  column: Partial<DCFColumn>,
  lovValueFormatter: (params?: any) => any
) => {
  const extractedDataMap = Object.assign({}, ...keyList.map(val => ({ [val]: true })));

  if (column.inputType === InputType.NAICS) {
    const result = traverseTree(column.values, keyList, { dataKey });
    if (result.length > 0) {
      return result.map(val => (lovValueFormatter ? lovValueFormatter(val) : val.value));
    }
    return [];
  } else {
    return column.values
      .filter(val => val[dataKey] != null && extractedDataMap[val[dataKey]])
      .map(val => (lovValueFormatter ? lovValueFormatter(val) : val.value));
  }
};

const getAllLovValues = (
  keyList: any[],
  dataKey: string,
  column: Partial<DCFColumn>,
  lovValueFormatter: (params?: any) => any,
  valueWhenEmpty = EMPTY_STRING
) => {
  // Make a mapping of the available LOVs
  const availableLOVsMap = Object.assign(
    {},
    ...(column.values ?? []).map(lovItem => ({ [lovItem[dataKey]]: lovItem }))
  );

  return keyList.map(keyItem =>
    lovValueFormatter
      ? lovValueFormatter(availableLOVsMap[keyItem])
      : (availableLOVsMap[keyItem]?.value ?? valueWhenEmpty)
  );
};

export const formatExtractedDataWhenArray = (
  extractedData: (string | number | Date | boolean)[],
  column: Partial<DCFColumn>,
  filterDuplicates: boolean,
  valueWhenEmpty = EMPTY_STRING
) => {
  const valueWhenEmptyToUse = valueWhenEmpty ?? DASH;
  if (extractedData.length === 0) return valueWhenEmptyToUse;

  if (filterDuplicates) {
    const isNotNullNoEmpty = val => val != null && val !== EMPTY_STRING;
    // For fields where the values come from an array of values, all the values could be null or empty
    const filteredNulls = extractedData.filter(isNotNullNoEmpty).map(value => sanitizeValue(value, column));
    if (filteredNulls.length === 0) {
      return valueWhenEmptyToUse;
    }
    const result = Array.from(new Set(filteredNulls));
    // Special case for company_contact_role_addresses not to sort the values and return as per sort order
    return (
      column?.field?.includes(COMPANY_CONTACT_PATH) || column?.field?.includes(RENT_ESCALATION_CUSTOM_PATH)
        ? result
        : result.sort((a, b) => {
            if (typeof a === 'number' && typeof b === 'number') {
              return a - b;
            }
            if (a instanceof Date && b instanceof Date) {
              return a.getTime() - b.getTime();
            }
            return String(a).localeCompare(String(b));
          })
    ).join(', ');
  }

  return Array.from(extractedData, value =>
    value != null ? sanitizeValue(value, column, valueWhenEmptyToUse) : valueWhenEmptyToUse
  ).join(', ');
};

const getFieldAppendSort = (column: Partial<DCFColumn>) => {
  const { diggerOptions, sortCondition } = column as any;

  let fieldAppendSort = column.getReadPath();
  if (diggerOptions?.['useJsonata'] && sortCondition) {
    const separatorIndex = fieldAppendSort.search(/[./]/);
    if (separatorIndex !== -1) {
      const sep = fieldAppendSort.charAt(separatorIndex);
      if (sep === '/') {
        // replace the first '/' with '.' when injecting the sortCondition so JSONata path stays valid
        fieldAppendSort =
          fieldAppendSort.slice(0, separatorIndex) + sortCondition + '.' + fieldAppendSort.slice(separatorIndex + 1);
      } else {
        fieldAppendSort =
          fieldAppendSort.slice(0, separatorIndex) + sortCondition + fieldAppendSort.slice(separatorIndex);
      }
    } else {
      fieldAppendSort = fieldAppendSort + sortCondition;
    }
  }
  return fieldAppendSort;
};

// MIQQLParser.parse(condition) only depends on the condition string and returns a pure evaluator function;
// digger wraps it in a fresh { condition, useJsonata } object per call and never mutates it, so it is safe
// to cache the parsed function by condition string instead of re-parsing it on every cell.
const parsedConditionCache = new Map<string, ReturnType<typeof MIQQLParser.parse>>();

const parseCondition = (condition: string): ReturnType<typeof MIQQLParser.parse> => {
  let parsed = parsedConditionCache.get(condition);
  if (parsed === undefined) {
    parsed = MIQQLParser.parse(condition);
    parsedConditionCache.set(condition, parsed);
  }
  return parsed;
};

const extractData = (data: any, column: Partial<DCFColumn>) => {
  let extractedData: any;
  const { field = '', condition, diggerOptions } = column as any;

  if (!column.path && simpleValueValidation(condition)) {
    let parsedCondition;
    try {
      parsedCondition = parseCondition(condition);
      extractedData = digger(data, field, parsedCondition);
    } catch (e) {
      if (parsedCondition != null) {
        logDataCellFormatterError(
          `Something went wrong while extracting data from ${field} with condition ${condition}`,
          e
        );
        extractedData = digger(data, field);
      } else {
        logDataCellFormatterError(
          // tslint:disable-next-line:max-line-length
          `Something went wrong while parsing the catalog condition for\n - field: '${column.header}'\n - condition: '${condition}'`,
          e
        );
      }
    }
  } else {
    // prevent failure of unimplemented ColumnFactory
    if (!column.getReadPath) {
      column = Object.assign(column, hasReadPath());
    }
    const fieldAppendSort = getFieldAppendSort(column);
    extractedData = digger(data, fieldAppendSort, diggerOptions);
  }

  return extractedData;
};
